import type { OnboardingStepDeps } from '@/composables/useOnboarding';
import i18n from '@/i18n';
import { createInfoStep } from '@/onboarding/stepStyles';
import type { StepOptions } from 'shepherd.js';
import type { Composer } from 'vue-i18n';

const t = (key: string, params: Record<string, unknown> = {}) => (i18n.global as unknown as Composer).t(key, params);

// Helper pour sélectionner le texte selon le rôle
const getRoleSpecificText = (baseKey: string, role: string): string => {
  if (role === 'admin') {
    const adminKey = `${baseKey}.textAdmin`;
    return t(adminKey);
  } else if (role === 'single_user') {
    const singleUserKey = `${baseKey}.textSingleUser`;
    return t(singleUserKey);
  } else if (role === 'employee') {
    const employeeKey = `${baseKey}.textEmployee`;
    return t(employeeKey);
  }
  // Fallback au texte de base
  return t(`${baseKey}.text`);
};

export function getIntroductionSteps({ tour, currentRole }: OnboardingStepDeps): StepOptions[] {
  return [
    // 1 — Accueil (informatif)
    createInfoStep({
      id: 'welcome',
      title: t('onboarding.chapters.introduction.steps.welcome.title'),
      text: t('onboarding.chapters.introduction.steps.welcome.text'),
      attachTo: { element: '#home-header', on: 'bottom' },
      canClickTarget: false,
      buttons: [{ text: t('onboarding.common.next'), action: () => tour.value?.next() }],
    }),

    // 2 — Le bloc "Fonctionnalités"
    createInfoStep({
      id: 'features-overview',
      title: t('onboarding.chapters.introduction.steps.featuresOverview.title'),
      text: getRoleSpecificText('onboarding.chapters.introduction.steps.featuresOverview', currentRole),
      canClickTarget: false,
      attachTo: { element: '#onboarding-feature', on: 'bottom' },
      buttons: [
        { text: t('onboarding.common.previous'), action: () => tour.value?.back(), secondary: true },
        { text: t('onboarding.common.next'), action: () => tour.value?.next() },
      ],
    }),

    // 3 — L'icône de Profil
    createInfoStep({
      id: 'profile-access',
      title: t('onboarding.chapters.introduction.steps.profileAccess.title'),
      text: getRoleSpecificText('onboarding.chapters.introduction.steps.profileAccess', currentRole),
      canClickTarget: false,
      attachTo: { element: '#onboarding-profile-button', on: 'bottom' },
      buttons: [
        { text: t('onboarding.common.previous'), action: () => tour.value?.back(), secondary: true },
        { text: t('onboarding.common.next'), action: () => tour.value?.next() },
      ],
    }),

    // 4 — La barre de navigation
    createInfoStep({
      id: 'main-navigation',
      title: t('onboarding.chapters.introduction.steps.mainNavigation.title'),
      text: t('onboarding.chapters.introduction.steps.mainNavigation.text'),
      canClickTarget: false,
      attachTo: { element: '#navbar', on: 'top' },
      buttons: [
        { text: t('onboarding.common.previous'), action: () => tour.value?.back(), secondary: true },
        { text: t('onboarding.common.next'), action: () => tour.value?.next() },
      ],
    }),
  ];
}
